// src/config/pedidos.js
// Configuraciones específicas para el módulo de pedidos
import { normalizarBooleano } from '../utils/general'

// Estados principales (secuenciales)
export const ESTADOS_PRINCIPALES = [
	{ key: 'recibido', label: 'Nuevo', color: 'secondary', icon: 'fa-inbox' },
	{ key: 'procesando', label: 'Procesando', color: 'warning', icon: 'fa-cog' },
	{ key: 'listo', label: 'Listo', color: 'info', icon: 'fa-check' },
	{ key: 'enCamino', label: 'En Camino', color: 'primary', icon: 'fa-truck', requiereEnvio: true },
	{ key: 'entregado', label: 'Entregado', color: 'success', icon: 'fa-home' }
]

// Estados especiales (no secuenciales)
export const ESTADOS_ESPECIALES = [
	{ key: 'pagado', label: 'Pagado', color: 'success', icon: 'fa-credit-card' },
	{ key: 'cancelado', label: 'Cancelado', color: 'danger', icon: 'fa-ban' }
]

// Tipos de pedido que requieren envío
// Incluye tipos del sistema anterior y del nuevo sistema de pagos
const TIPOS_CON_ENVIO = ['envio', 'online-envio', 'envio-interior']
// Nota: 'retiro' NO requiere envío (el cliente retira en tienda)

// Función para determinar si un tipo de pedido requiere envío
export function requiereEnvio(tipoPedido) {
	return TIPOS_CON_ENVIO.some(tipo => tipoPedido?.toLowerCase().includes(tipo))
}

// Obtener la configuración de un estado por su key
export function getConfigEstado(estadoKey) {
	return [...ESTADOS_PRINCIPALES, ...ESTADOS_ESPECIALES].find(e => e.key === estadoKey)
}

// Obtener el próximo estado en la secuencia
export function getProximoEstado(estadoActual, tipoPedido) {
	const indiceActual = ESTADOS_PRINCIPALES.findIndex(e => e.key === estadoActual)

	if (indiceActual === -1 || indiceActual >= ESTADOS_PRINCIPALES.length - 1) {
		return null // No hay próximo estado
	}

	// Buscar el próximo estado válido
	for (let i = indiceActual + 1; i < ESTADOS_PRINCIPALES.length; i++) {
		const estado = ESTADOS_PRINCIPALES[i]

		// Saltar "enCamino" si no requiere envío
		if (estado.key === 'enCamino' && !requiereEnvio(tipoPedido)) continue

		return estado.key
	}

	return null
}

// Verificar si se puede avanzar a un estado específico
export function puedeAvanzarA(estadoActual, estadoDestino, tipoPedido) {
	// No se puede avanzar si está cancelado
	if (estadoActual === 'cancelado') return false

	// Casos especiales
	if (estadoDestino === 'cancelado') return true // Siempre se puede cancelar
	if (estadoDestino === 'pagado') return true // Siempre se puede marcar/desmarcar pago

	// Para estados secuenciales, verificar que sea el próximo en la secuencia
	const indiceActual = ESTADOS_PRINCIPALES.findIndex(e => e.key === estadoActual)
	const indiceDestino = ESTADOS_PRINCIPALES.findIndex(e => e.key === estadoDestino)

	if (indiceActual === -1 || indiceDestino === -1) return false

	// Solo se puede avanzar al siguiente estado
	const diferencia = indiceDestino - indiceActual

	// Caso especial: saltar "enCamino" si no requiere envío
	if (estadoDestino === 'enCamino' && !requiereEnvio(tipoPedido)) return false
	if (estadoActual === 'listo' && estadoDestino === 'entregado' && !requiereEnvio(tipoPedido)) {
		return true // Puede ir directo de "listo" a "entregado"
	}

	return diferencia === 1 // Solo avanzar al siguiente
}

// ✅ NUEVAS FUNCIONES PARA CRONOLOGÍA INTERACTIVA

// Verificar si se puede marcar un estado (hacia adelante)
export function puedeMarcarEstado(estadoActual, estadoDestino, tipoPedido) {
	if (estadoDestino === 'recibido') return false // Ya está marcado por defecto

	const proximoEstado = getProximoEstado(estadoActual, tipoPedido)
	return proximoEstado === estadoDestino
}

// Verificar si se puede desmarcar un estado (hacia atrás)
export function puedeDesmarcarEstado(estadoKey, estados, tipoPedido) {
	if (estadoKey === 'recibido') return false // Nunca se puede desmarcar
	if (!estados[estadoKey]) return false // No está marcado

	// Verificar que no haya estados posteriores marcados
	const indiceActual = ESTADOS_PRINCIPALES.findIndex(e => e.key === estadoKey)

	for (let i = indiceActual + 1; i < ESTADOS_PRINCIPALES.length; i++) {
		const estadoPosterior = ESTADOS_PRINCIPALES[i]

		// Saltar "enCamino" si no requiere envío
		if (estadoPosterior.key === 'enCamino' && !requiereEnvio(tipoPedido)) continue

		if (estados[estadoPosterior.key]) {
			return false // Hay un estado posterior marcado
		}
	}

	return true
}

// Determinar el próximo estado que se puede marcar
export function getEstadoMarcable(pedido) {
	const estadoActual = calcularEstadoPrincipal(pedido)
	const estados = calcularEstados(pedido)

	// Si está cancelado, no se puede marcar nada
	if (estados.cancelado) return null

	return getProximoEstado(estadoActual, pedido.tipo)
}

// Obtener las acciones disponibles para un pedido
export function getAccionesDisponibles(pedido) {
	const estadoActual = calcularEstadoPrincipal(pedido)
	const estados = calcularEstados(pedido) // Usar estados normalizados
	const acciones = []

	// Si está cancelado, solo mostrar acciones de visualización
	if (estadoActual === 'cancelado') {
		return acciones
	}

	// Acción de cancelar (siempre disponible si no está cancelado)
	acciones.push({
		key: 'cancelar',
		label: 'Cancelar pedido',
		icon: 'fa-ban',
		variant: 'warning'
	})

	// Obtener próximo estado
	const proximoEstado = getProximoEstado(estadoActual, pedido.tipo)
	if (proximoEstado) {
		const configProximo = getConfigEstado(proximoEstado)
		acciones.push({
			key: proximoEstado,
			label: `Marcar como ${configProximo.label.toLowerCase()}`,
			icon: configProximo.icon,
			variant: 'primary'
		})
	}

	// Acción de pago/despago
	if (estados.pagado) {
		acciones.push({
			key: 'despagar',
			label: 'Marcar como no pagado',
			icon: 'fa-credit-card',
			variant: 'outline-warning'
		})
	} else {
		acciones.push({
			key: 'pagar',
			label: 'Marcar como pagado',
			icon: 'fa-credit-card',
			variant: 'success'
		})
	}

	return acciones
}

// Funciones de compatibilidad con el código existente
export function getTipoColor(tipo) {
	const colores = {
		// Grupo ENVÍOS (tonos azules)
		'online-envio': 'info',        // Azul claro
		'envio': 'primary',           // Azul medio
		'envio-interior': 'dark',     // Azul oscuro

		// Grupo RETIROS (tonos verdes/gris)
		'online-tienda': 'success',   // Verde
		'tienda': 'secondary',        // Gris

		// Nuevos tipos del sistema de pagos actualizado
		'retiro': 'success'           // Verde (retiro en tienda)
	}

	return colores[tipo] || 'light' // Default para tipos desconocidos
}

export function getTipoTexto(tipo) {
	const textos = {
		'online-envio': 'Pago online con envío a domicilio',
		'online-tienda': 'Pago online con retiro en local',
		'envio': 'Envío a domicilio',
		'envio-interior': 'Envío al interior',
		'tienda': 'Pago en sucursal y pasar a buscar',
		// Nuevos tipos del sistema de pagos actualizado
		'retiro': 'Retiro en tienda'
	}

	return textos[tipo] || tipo || 'No especificado'
}

// También actualizar getEstadoColor para que use los colores del ESTADOS_PRINCIPALES:
export function getEstadoColor(estado) {
	const config = getConfigEstado(estado)
	return config?.color || 'secondary'
}

export function getEstadoTexto(estado) {
	const config = getConfigEstado(estado)
	return config?.label || estado
}

// Función para calcular todos los estados (para compatibilidad)
export const calcularEstados = (pedido) => {
	if (!pedido) return {}

	return {
		// Estados con fechas - si tienen valor son fechas, si son null/undefined son false
		procesando: pedido.procesando || null,
		listo: pedido.listo || null,
		enCamino: pedido.enCamino || null,
		entregado: pedido.entregado || null,
		cancelado: pedido.cancelado || null, // CORREGIDO: cancelado es fecha, no booleano

		// Estados booleanos
		pagado: normalizarBooleano(pedido.pagado),
		finalizado: normalizarBooleano(pedido.finalizado)
	}
}

// Función helper para verificar si un pedido está cancelado
export const estaCancelado = (pedido) => {
	return !!(pedido.cancelado) // Convierte fecha a booleano
}

// Función helper para verificar si un pedido está finalizado
export const estaFinalizado = (pedido) => {
	return normalizarBooleano(pedido.finalizado)
}

// Actualizar calcularEstadoPrincipal para manejar correctamente cancelado
export const calcularEstadoPrincipal = (pedido) => {
	if (!pedido) return 'recibido'

	// Verificar cancelado PRIMERO (tiene prioridad sobre otros estados)
	if (pedido.cancelado) {
		return 'cancelado'
	}

	// Verificar finalizado
	if (normalizarBooleano(pedido.finalizado)) {
		return 'finalizado'
	}

	// Estados secuenciales (verificar en orden inverso)
	if (pedido.entregado) return 'entregado'
	if (pedido.enCamino) return 'enCamino'
	if (pedido.listo) return 'listo'
	if (pedido.procesando) return 'procesando'

	// Estado inicial
	return 'recibido'
}

// Configuración existente (mantener para compatibilidad)
export const PEDIDOS_FETCH_CUANTOS = 25
export const OPCIONES_PAGINACION_NUM_RESULTADOS = [10, 25, 50, 100]

export const OPCIONES_TIPOS_PEDIDO_PARA_FILTRO = [
	{ value: 'online-envio', label: 'Pago online con envío a domicilio' },
	{ value: 'online-tienda', label: 'Pago online con retiro en local' },
	{ value: 'envio', label: 'Envío a domicilio' },
	{ value: 'envio-interior', label: 'Envío al interior' },
	{ value: 'tienda', label: 'Pago en sucursal y pasar a buscar' },
	// Nuevos tipos del sistema de pagos actualizado
	{ value: 'retiro', label: 'Retiro en tienda' }
]

export const OPCIONES_MOTIVOS_CANCELACION = [
	{ label: 'Cliente canceló el pedido' },
	{ label: 'Producto no disponible' },
	{ label: 'Error en el pedido' },
	{ label: 'Problemas de pago' },
	{ label: 'No recibimos comprobante de pago a tiempo' },
	{ label: 'Demora en la entrega' },
	{ label: 'Error del sistema' }
]